import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Col,
  Modal,
  ModalBody,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import { formatDateLabel } from "../../utils/dateOnly";
import { ehNfse, rotuloDocumento } from "../../utils/modeloDocumento";
import {
  AUTOPILOT_COLORS,
  autopilotEyebrowStyle,
  autopilotInsetCardStyle,
  autopilotTitleStyle,
} from "../autopilot/styles";
import LimiteDivergenciaValue from "./LimiteDivergenciaValue";
import TributoIncideBadge from "./TributoIncideBadge";
import CanceladoSefazBadge, {
  CANCELADO_SEFAZ_TITLE,
  isCanceladoSefaz,
} from "./CanceladoSefazBadge";
import FinancialComponentComparisonTable from "./FinancialComponentComparisonTable";
import RecalcularFreteCteButton from "./RecalcularFreteCteButton";
import { FaturaDoCteAba, FaturaDoCteResumo } from "./FaturaDoCte";
import VerXmlCteButton from "./VerXmlCteButton";
import TabelaFreteModal from "../../views/Empresa/TabelasFrete/TabelaFreteModal";
import NotaFiscalDetailModal from "../../views/Empresa/ConsultaNotasFiscais/NotaFiscalDetailModal";
import { useAuth } from "../../hooks/auth";
import "./CteAuditDetailModal.css";

function formatCurrency(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  const numeric = Number(value);
  const normalized = Math.abs(numeric) < 0.005 ? 0 : numeric;
  return normalized.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatWeight(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} kg`;
}

function formatBooleanLabel(value) {
  if (value === true || value === 1 || value === "1") return "Sim";
  if (value === false || value === 0 || value === "0") return "Não";
  return "-";
}

function formatPercent(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "-";
  return `${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function formatAuditValue(value, kind) {
  if (value === null || value === undefined || value === "") return "-";
  if (kind === "peso") return formatWeight(value);
  if (kind === "currency") return formatCurrency(value);
  return String(value);
}

function formatSimulatedPriceTables(summary) {
  if (!summary?.tabelas?.length) return "-";
  return summary.tabelas
    .map((item) => item?.nome || item?.servico)
    .filter(Boolean)
    .join(" | ");
}

// PO1-3400: mesma permissão do menu Tabelas de frete (Pages.Recalculo); admin
// Everlog tem bypass, como no menu.
const PERMISSAO_TABELAS_FRETE = "Pages.Recalculo";

export function podeAbrirTabelaFrete(user) {
  if (!user) return false;
  if (user.setor === "admin") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(PERMISSAO_TABELAS_FRETE);
}

const tabelaPrecoLinkStyle = {
  background: "none",
  border: 0,
  padding: 0,
  color: AUTOPILOT_COLORS.text,
  font: "inherit",
  textAlign: "left",
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: "3px",
  cursor: "pointer",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

// PO1-3400: cada tabela que calculou o simulado vira link e abre a tabela em
// modal por cima do detalhe do CT-e, sem trocar de menu.
export function SimulatedPriceTablesValue({ summary, onOpenTabela }) {
  const tabelas = (summary?.tabelas || []).filter((item) => item?.nome || item?.servico);
  if (!tabelas.length) return "-";
  if (!onOpenTabela) return formatSimulatedPriceTables(summary);

  return tabelas.map((item, index) => {
    const label = item.nome || item.servico;
    return (
      <React.Fragment key={item.id || `${label}-${index}`}>
        {index > 0 && " | "}
        {item.id ? (
          <button
            type="button"
            style={tabelaPrecoLinkStyle}
            title="Abrir a tabela de preço"
            onClick={() => onOpenTabela(item.id)}
          >
            {label}
          </button>
        ) : (
          label
        )}
      </React.Fragment>
    );
  });
}

function toNumberOrNull(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return null;
  return Number(value);
}

function buildLocationDisplay(city, uf) {
  const parts = [city, uf].filter(Boolean);
  return parts.length ? parts.join(" / ") : "-";
}

function getFinancialDifferenceSummary({ simulado, receber, fallbackDifference, divergenteCte }) {
  const numericSimulado = toNumberOrNull(simulado);
  const numericReceber = toNumberOrNull(receber);
  const fallback = toNumberOrNull(fallbackDifference);

  const directionalDifference =
    numericSimulado !== null && numericReceber !== null
      ? numericSimulado - numericReceber
      : null;

  const magnitude =
    directionalDifference !== null
      ? Math.abs(directionalDifference)
      : fallback !== null
        ? Math.abs(fallback)
        : null;

  // O CT-e ja carrega o veredito de divergencia (Sta_Divergencia), que respeita o
  // limite percentual da TMS. Quando ele diz que NAO e divergente, uma diferenca de
  // arredondamento de rateio (ex.: 2 centavos) nao deve disparar "simulado acima":
  // o autoritativo e o CT-e, nao o corte fixo de 1 centavo. Sem a flag (backend sem
  // o campo), cai no comportamento antigo por magnitude.
  const ctENaoDivergente = [false, 0, "0"].includes(divergenteCte);

  let direction = "unknown";
  if (ctENaoDivergente) {
    direction = "balanced";
  } else if (directionalDifference !== null) {
    if (directionalDifference > 0.01) direction = "simulado_above";
    else if (directionalDifference < -0.01) direction = "receber_above";
    else direction = "balanced";
  } else if (magnitude !== null && magnitude <= 0.01) {
    direction = "balanced";
  }

  return {
    magnitude,
    displayValue: direction === "unknown" ? magnitude : directionalDifference,
    direction,
    isMeaningful: !ctENaoDivergente && magnitude !== null && magnitude > 0.01,
  };
}

const AUDIT_STATUS_META = {
  ok: {
    label: "OK",
    color: "#00bf9a",
    background: "rgba(0, 191, 154, 0.14)",
  },
  divergente: {
    label: "Divergente",
    color: "#fd5d93",
    background: "rgba(253, 93, 147, 0.14)",
  },
  sem_dado: {
    label: "Sem dado",
    color: "#ffb86b",
    background: "rgba(255, 184, 107, 0.14)",
  },
  informativo: {
    label: "Informativo",
    color: "#1d8cf8",
    background: "rgba(29, 140, 248, 0.14)",
  },
};

const DETAIL_DIAGNOSIS_META = {
  critical: {
    badge: "Ação imediata",
    color: "#fd5d93",
    border: "rgba(253, 93, 147, 0.42)",
    background: "rgba(253, 93, 147, 0.14)",
    glow: "rgba(253, 93, 147, 0.16)",
  },
  warning: {
    badge: "Atenção",
    color: "#ffb86b",
    border: "rgba(255, 184, 107, 0.4)",
    background: "rgba(255, 184, 107, 0.14)",
    glow: "rgba(255, 184, 107, 0.16)",
  },
  healthy: {
    badge: "Consistente",
    color: "#00bf9a",
    border: "rgba(0, 191, 154, 0.38)",
    background: "rgba(0, 191, 154, 0.12)",
    glow: "rgba(0, 191, 154, 0.14)",
  },
  neutral: {
    badge: "Em análise",
    color: "#4db5ff",
    border: "rgba(77, 181, 255, 0.38)",
    background: "rgba(77, 181, 255, 0.12)",
    glow: "rgba(77, 181, 255, 0.14)",
  },
};

const sectionCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.95rem 1rem",
};

const sectionTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "0.96rem",
  fontWeight: 700,
  marginBottom: "0.25rem",
};

const sectionDescriptionStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.82rem",
  lineHeight: 1.5,
  marginBottom: "0.85rem",
};

const metricCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.85rem 0.95rem",
};

const metricLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.74rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  marginBottom: "0.3rem",
};

const metricValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.2,
};

const tableHeadCellStyle = {
  color: AUTOPILOT_COLORS.muted,
  borderColor: AUTOPILOT_COLORS.border,
  fontWeight: 600,
  background: "var(--everlog-card-surface-field)",
  position: "sticky",
  top: 0,
  zIndex: 1,
  whiteSpace: "nowrap",
};

// PO1-3474: células do detalhe não quebram linha (a tabela rola na horizontal).
const detailTableCellStyle = {
  color: AUTOPILOT_COLORS.text,
  borderColor: AUTOPILOT_COLORS.border,
  verticalAlign: "middle",
  background: "var(--everlog-card-surface-elevated)",
  whiteSpace: "nowrap",
};

const nfLinkStyle = {
  background: "none",
  border: 0,
  padding: 0,
  color: AUTOPILOT_COLORS.accent,
  fontWeight: 700,
  textDecoration: "underline",
  cursor: "pointer",
};

const detailModalCloseButtonStyle = {
  border: 0,
  background: "transparent",
  color: AUTOPILOT_COLORS.title,
  fontSize: "2rem",
  lineHeight: 1,
  padding: 0,
  cursor: "pointer",
  opacity: 0.95,
  textShadow: "none",
};

const detailTabStyle = (active) => ({
  borderRadius: 999,
  border: `1px solid ${active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
  background: active ? "rgba(77, 181, 255, 0.14)" : AUTOPILOT_COLORS.insetSurface,
  color: active ? AUTOPILOT_COLORS.title : AUTOPILOT_COLORS.muted,
  fontSize: "0.78rem",
  fontWeight: 600,
  padding: "0.42rem 0.82rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const detailDiagnosisCardStyle = (meta) => ({
  ...sectionCardStyle,
  padding: "1rem 1rem 1.05rem",
  marginBottom: "1rem",
  border: `1px solid ${meta.border}`,
  // PO1-3746: fundo pelo token da superfície (o gradiente escuro fixo apagava o texto no tema claro).
  background: `linear-gradient(135deg, ${meta.glow} 0%, var(--everlog-card-surface-elevated) 60%)`,
  boxShadow: "none",
});

const detailDiagnosisBadgeStyle = (meta) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  borderRadius: 999,
  border: `1px solid ${meta.border}`,
  background: meta.background,
  color: meta.color,
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  padding: "0.42rem 0.75rem",
  whiteSpace: "nowrap",
});

const detailDiagnosisTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.2,
  margin: "0.8rem 0 0.35rem",
};

const detailDiagnosisDescriptionStyle = {
  color: AUTOPILOT_COLORS.text,
  fontSize: "0.9rem",
  lineHeight: 1.55,
  marginBottom: "0.9rem",
  width: "100%",
  maxWidth: "100%",
};

const overviewPanelStyle = {
  padding: "0.2rem",
  height: "100%",
  border: 0,
  background: "transparent",
  boxShadow: "none",
};

const overviewPanelTitleStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1rem",
  fontWeight: 700,
  marginBottom: "0.24rem",
};

const overviewPanelDescriptionStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.8rem",
  lineHeight: 1.45,
  marginBottom: "0.85rem",
};

const overviewPanelEyebrowStyle = {
  color: AUTOPILOT_COLORS.accent,
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: "0.45rem",
};

const overviewFieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))",
  gap: "0.8rem 1rem",
};

const overviewFieldStyle = {
  minWidth: 0,
};

// PO1-3746: cores pelos tokens do tema (os valores fixos quase brancos sumiam no tema claro).
const overviewFieldLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.67rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: "0.42rem",
};

const overviewFieldValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1.08rem",
  fontWeight: 700,
  lineHeight: 1.28,
};

const overviewFieldMutedValueStyle = {
  color: AUTOPILOT_COLORS.text,
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: 1.42,
};

const overviewRouteCardStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.95rem 1rem",
  background: "var(--everlog-card-surface-field)",
  borderColor: "var(--everlog-card-border)",
  boxShadow: "none",
  marginBottom: "0.85rem",
};

const overviewRouteLabelStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  marginBottom: "0.4rem",
};

const overviewRouteValueStyle = {
  color: AUTOPILOT_COLORS.title,
  fontSize: "1rem",
  fontWeight: 700,
  lineHeight: 1.35,
};

const overviewMiniMetricStyle = {
  ...autopilotInsetCardStyle,
  padding: "0.82rem 0.88rem",
  border: "1px solid var(--everlog-card-border)",
  background: "var(--everlog-card-surface-field)",
  boxShadow: "none",
};

const overviewMiniMetricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "0.75rem",
};

const overviewSectionCardStyle = {
  background: "var(--everlog-card-surface-elevated)",
  border: "1px solid var(--everlog-card-border)",
  boxShadow: "none",
};

const overviewKeyHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.65rem",
  marginBottom: "0.28rem",
};

const overviewCopyButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 28,
  minWidth: 28,
  padding: "0 0.55rem",
  borderRadius: 999,
  border: "1px solid var(--everlog-card-border)",
  background: "var(--everlog-card-surface-field)",
  color: AUTOPILOT_COLORS.accent,
  fontSize: "0.75rem",
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "none",
};

function getCteDetailDiagnosis({ summary, auditoria, auditoriaFinanceira, notasFiscais }) {
  if (!summary) {
    return {
      tone: "neutral",
      title: "Análise do CTe",
      description: "Os detalhes do documento serão organizados aqui assim que a consulta for concluída.",
    };
  }

  const divergente = Number(auditoria?.resumo?.divergente || 0);
  const semDado = Number(auditoria?.resumo?.semDado || 0);
  const totalNotas = Number(summary?.qtdNotasFiscais || 0) || (notasFiscais || []).length;
  const financialSummary = getFinancialDifferenceSummary({
    simulado: auditoriaFinanceira?.resumo?.totalSimulado ?? summary?.valorFreteSimulado,
    receber: auditoriaFinanceira?.resumo?.totalCte ?? summary?.valorFreteReceber,
    fallbackDifference: auditoriaFinanceira?.resumo?.diferenca ?? summary?.valorDivergencia,
    divergenteCte: summary?.staDivergencia,
  });
  const hasFinancialDiff = financialSummary.isMeaningful;
  if (!totalNotas) {
    return {
      tone: "critical",
      title: "CTe sem base suficiente para auditoria",
      description: "Não há NFes vinculadas para sustentar a comparação do documento. O primeiro passo é validar o vínculo das notas e a origem da integração.",
    };
  }

  // PO1-3071: CT-e cancelado na SEFAZ e critico ANTES do financeiro. O fato fiscal
  // independe de o valor bater: o documento nao existe mais e nao deve ser pago. Sem
  // isto, um cancelado com valor conferindo caia no ramo "divergente > 0" da PO1-2502 e
  // o cabecalho dizia "Frete consistente" em verde, contradizendo o alerta vermelho logo
  // abaixo — sinal conflitante numa tela de aprovacao de pagamento.
  if (isCanceladoSefaz(summary)) {
    return {
      id: "cancelado_sefaz",
      tone: "critical",
      title: "CTe cancelado na SEFAZ",
      description:
        "A SEFAZ registrou o cancelamento deste CT-e, mas ele não pôde ser removido do Hub porque está vinculado a fatura. Não aprove o pagamento: marque NOK na conferência da fatura para o cancelamento entrar no próximo ciclo.",
    };
  }

  if (hasFinancialDiff) {
    const differenceLabel = formatCurrency(financialSummary.magnitude);
    const directionalDescription =
      financialSummary.direction === "simulado_above"
        ? `O simulado está ${differenceLabel} acima do frete a receber. Vale abrir a composição financeira antes de revisar os detalhes cadastrais.`
        : financialSummary.direction === "receber_above"
          ? `O frete a receber está ${differenceLabel} acima do simulado. Vale revisar cobrança, tabela aplicada e composição do cálculo antes de seguir.`
          : `O total confrontado entre CTe e simulado aponta diferença de ${differenceLabel}. Vale abrir a composição financeira antes de revisar os detalhes cadastrais.`;

    return {
      tone:
        financialSummary.direction === "simulado_above" || financialSummary.direction === "receber_above"
          ? "critical"
          : "warning",
      title:
        financialSummary.direction === "simulado_above"
          ? "Simulado acima do frete a receber"
          : financialSummary.direction === "receber_above"
            ? "Frete a receber acima do simulado"
            : "Diferença financeira identificada neste CTe",
      description: directionalDescription,
    };
  }

  if (divergente > 0) {
    // PO1-2502: aqui o financeiro JA esta consistente (passou pelo hasFinancialDiff).
    // Os checks divergentes sao nao-financeiros (peso, modal, valor da mercadoria, origem/
    // destino) e nao afetam o valor cobrado, entao nao alarmam (amarelo): o cabecalho fica
    // consistente (verde) com observacao suave, e os checks seguem visiveis (informativos).
    // PO1-3071: o check situacao_sefaz NAO entra aqui — ele e tratado como critico acima,
    // porque cancelado na SEFAZ nao e observacao de qualidade de dado.
    return {
      tone: "healthy",
      title: "Frete consistente, com observações na composição",
      description: "O comparativo financeiro (frete a receber x simulado) está dentro do limite. Há divergências não-financeiras que não afetam o valor cobrado e seguem visíveis abaixo como informativas, úteis para checar a qualidade do dado (ex.: NF vinculada incorreta).",
    };
  }

  if (semDado > 0) {
    return {
      tone: "warning",
      title: "Faltam dados para fechar a conferência com segurança",
      description: "Parte da auditoria está sem base completa. O documento pode até parecer consistente, mas ainda depende de dados ausentes para uma validação confiável.",
    };
  }

  return {
    tone: "healthy",
    title: "CTe consistente com a composição atual",
    description: "Os checks principais e a comparação financeira estão alinhados com a base disponível.",
  };
}

async function copyTextToClipboard(value) {
  if (!value) return false;

  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(String(value));
      return true;
    }
  } catch (error) {
    // fallback below
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = String(value);
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch (error) {
    return false;
  }
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div
      style={{
        border: "1px solid rgba(253, 93, 147, 0.35)",
        background: "rgba(253, 93, 147, 0.12)",
        color: AUTOPILOT_COLORS.title,
        borderRadius: 12,
        padding: "0.75rem 0.9rem",
        marginBottom: "1rem",
        fontSize: "0.84rem",
      }}
    >
      {message}
    </div>
  );
}

function SectionCard({ title, description, action, children, style }) {
  return (
    <div className="cte-audit-detail-section" style={{ ...sectionCardStyle, ...style }}>
      {(title || description || action) && (
        <div
          className="cte-audit-detail-section__head"
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "0.75rem",
            alignItems: "flex-start",
            marginBottom: children ? "0.8rem" : 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title ? <div className="cte-audit-detail-section__title" style={sectionTitleStyle}>{title}</div> : null}
            {description ? <div className="cte-audit-detail-section__description" style={sectionDescriptionStyle}>{description}</div> : null}
          </div>
          {action ? <div>{action}</div> : null}
        </div>
      )}
      {children}
    </div>
  );
}

function DetailMetric({ label, value, color }) {
  return (
    <div className="cte-audit-detail-metric" style={metricCardStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={{ ...metricValueStyle, color: color || AUTOPILOT_COLORS.title, fontSize: "0.96rem" }}>
        {value}
      </div>
    </div>
  );
}

function DetailField({ label, value, valueStyle }) {
  const displayValue =
    value === 0 || value === false ? value : value !== null && value !== undefined && value !== "" ? value : "-";

  return (
    <div className="cte-audit-detail-field" style={{ marginBottom: "0.9rem" }}>
      <div style={metricLabelStyle}>{label}</div>
      <div
        style={{
          color: AUTOPILOT_COLORS.text,
          fontSize: "0.96rem",
          lineHeight: 1.45,
          fontWeight: 600,
          minWidth: 0,
          ...valueStyle,
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

// Base do peso usada no cálculo (check "peso"): cubado quando a transportadora
// declarou peso base acima do bruto das NFes; bruto quando o cálculo usou o bruto.
const WEIGHT_BASIS_META = {
  cubado: {
    label: "cubado",
    color: "var(--everlog-status-info)",
    title: "Peso base do cálculo acima do peso bruto das NFes (cubagem declarada no CTe)",
  },
  bruto: {
    label: "bruto",
    color: "var(--everlog-card-muted)",
    title: "Cálculo feito sobre o peso bruto das NFes",
  },
};

function WeightBasisBadge({ basis }) {
  const meta = WEIGHT_BASIS_META[basis];
  if (!meta) return null;

  return (
    <span
      title={meta.title}
      style={{
        display: "inline-block",
        marginLeft: "0.4rem",
        borderRadius: 999,
        padding: "0.1rem 0.45rem",
        fontSize: "0.62rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        lineHeight: 1.4,
        color: meta.color,
        border: `1px solid ${meta.color}`,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

function AuditWeightCell({ check }) {
  const bruto = check.nfesPesoBruto;
  const showBruto =
    bruto != null && check.nfesValor != null && Number(bruto) !== Number(check.nfesValor);

  return (
    <>
      {formatAuditValue(check.nfesValor, "peso")}
      <WeightBasisBadge basis={check.nfesBase} />
      {showBruto ? (
        <div style={{ fontSize: "0.72rem", color: "var(--everlog-card-muted)", marginTop: 2 }}>
          bruto NFes: {formatWeight(bruto)}
        </div>
      ) : null}
    </>
  );
}

function AuditStatusBadge({ status }) {
  const meta = AUDIT_STATUS_META[status] || AUDIT_STATUS_META.sem_dado;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 94,
        borderRadius: 999,
        padding: "0.35rem 0.7rem",
        fontSize: "0.74rem",
        fontWeight: 700,
        color: meta.color,
        background: meta.background,
        border: `1px solid ${meta.color}`,
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

export default function CteAuditDetailModal({
  isOpen,
  onClose,
  loading,
  error,
  detail,
  row,
  // Opcional: ({ idFreteTaxa, idComponentePrestacaoServico }) => Promise. Quando
  // informado, a tabela de componentes permite criar o de/para taxa <-> componente
  // arrastando um sobre o outro; o pai faz o POST e recarrega `detail`.
  onLinkComponent,
  // Opcional (PO1-3197): chamado depois de um cálculo/recálculo com sucesso, para
  // o pai recarregar `detail` (e a lista de onde o CT-e veio).
  onRecalculated,
}) {
  const { user } = useAuth();
  const [detailTab, setDetailTab] = useState("visao-geral");
  const [detailKeyCopyState, setDetailKeyCopyState] = useState("idle");
  // PO1-3400: tabela de preço aberta por cima do detalhe (null = fechada).
  const [tabelaFreteAbertaId, setTabelaFreteAbertaId] = useState(null);
  const [nfAbertaId, setNfAbertaId] = useState(null);
  const copyButtonId = useMemo(
    () => `copy-cte-access-key-${Math.random().toString(36).slice(2)}`,
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setDetailTab("visao-geral");
      setDetailKeyCopyState("idle");
      setTabelaFreteAbertaId(null);
    }
  }, [isOpen]);

  const detailSummary = detail?.summary || null;
  // PO1-3367: fatura do CT-e (situação, decisão e histórico da conferência).
  const detailFatura = detail?.fatura || null;
  const detailNfs = detail?.notasFiscais || [];
  const detailPartes = detail?.partes || {};
  const detailComponentes = detail?.componentes || [];
  const detailImpostos = detail?.impostos || [];
  const detailCarga = detail?.carga || [];
  const detailAuditoria = detail?.auditoria || { resumo: null, checks: [] };
  const detailAuditoriaFinanceira = detail?.auditoriaFinanceira || {
    resumo: null,
    tabelaPrecoSimulado: null,
    grupos: [],
    componentesComparativo: [],
    frete: { itens: [], cteSelecionado: null, simuladoSelecionado: null },
    taxas: { itens: [] },
    tributos: { itens: [] },
  };
  const detailFinancialGroups = (detailAuditoriaFinanceira?.grupos || []).filter(
    (check) => check.id !== "tributos" || check.tributoIncideNoTotal !== false
  );
  const detailAdicionais = detail?.adicionais || null;
  const detailFinancialSummary = getFinancialDifferenceSummary({
    simulado: detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary?.valorFreteSimulado,
    receber: detailAuditoriaFinanceira?.resumo?.totalCte ?? detailSummary?.valorFreteReceber,
    fallbackDifference: detailAuditoriaFinanceira?.resumo?.diferenca ?? detailSummary?.valorDivergencia,
    divergenteCte: detailSummary?.staDivergencia,
  });
  const detailFinancialDifferenceColor =
    detailFinancialSummary.magnitude === null
      ? AUTOPILOT_COLORS.text
      : detailFinancialSummary.isMeaningful
        ? "#fd5d93"
        : "#00bf9a";
  const detailOriginLabel = buildLocationDisplay(detailSummary?.cidadeOrigem, detailSummary?.ufOrigem);
  const detailDestinationLabel = buildLocationDisplay(detailSummary?.cidadeDestino, detailSummary?.ufDestino);
  const detailDiagnosis = getCteDetailDiagnosis({
    summary: detailSummary,
    auditoria: detailAuditoria,
    auditoriaFinanceira: detailAuditoriaFinanceira,
    notasFiscais: detailNfs,
  });
  const detailDiagnosisMeta =
    DETAIL_DIAGNOSIS_META[detailDiagnosis.tone] || DETAIL_DIAGNOSIS_META.neutral;
  const detailTabs = [
    { id: "visao-geral", label: "Visão geral" },
    { id: "auditoria", label: "Auditoria" },
    { id: "notas", label: "NFes" },
    { id: "fatura", label: "Fatura" },
  ];
  const fallbackTitle = row?.numeroCTe || row?.idCTeValue || "Documento não identificado";

  const handleCopyDetailAccessKey = useCallback(async () => {
    const value = detailSummary?.chaveAcesso;
    if (!value) return;

    const copied = await copyTextToClipboard(value);
    setDetailKeyCopyState(copied ? "copied" : "error");
  }, [detailSummary]);

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      toggle={onClose}
      size="xl"
      modalClassName="modal-align-top cte-audit-detail-modal"
      fade={false}
      autoFocus={false}
    >
      <div
        className="cte-audit-detail-header"
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "0.8rem",
          padding: "1rem 1.25rem 0.75rem",
          borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={autopilotEyebrowStyle}>Detalhe do {rotuloDocumento(detailSummary ? detailSummary.modelo : row && row.modelo)}</div>
          <div style={{ ...autopilotTitleStyle, fontSize: "1.1rem", marginBottom: "0.15rem" }}>
            {detailSummary
              ? `${detailSummary.numero || "-"}${detailSummary.serie ? ` / ${detailSummary.serie}` : ""}`
              : fallbackTitle}
          </div>
        </div>
        <div className="cte-audit-detail-header__actions" style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
          {detailSummary ? <VerXmlCteButton summary={detailSummary} /> : null}
          {detailSummary ? (
            <RecalcularFreteCteButton
              summary={detailSummary}
              simulado={detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary.valorFreteSimulado}
              bloqueio={detailFatura?.bloqueioRecalculo}
              onRecalculated={onRecalculated}
            />
          ) : null}
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="cte-audit-detail-close"
            style={detailModalCloseButtonStyle}
          >
            ×
          </button>
        </div>
      </div>
      <ModalBody
        className="cte-audit-detail-body"
        style={{
          background: "var(--everlog-card-surface)",
          color: AUTOPILOT_COLORS.text,
          padding: "1rem 1rem 1.25rem",
          maxHeight: "82vh",
          overflowY: "auto",
        }}
      >
        <ErrorBanner message={error} />

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem 0 2.4rem" }}>
            <ClipLoader size={24} color={AUTOPILOT_COLORS.accent} />
          </div>
        ) : detailSummary ? (
          <>
            <div className="cte-audit-detail-diagnosis" style={detailDiagnosisCardStyle(detailDiagnosisMeta)}>
              <div style={detailDiagnosisBadgeStyle(detailDiagnosisMeta)}>
                {detailDiagnosisMeta.badge}
              </div>
              <div style={{ ...detailDiagnosisTitleStyle, color: detailDiagnosisMeta.color }}>
                {detailDiagnosis.title}
              </div>
              <div style={detailDiagnosisDescriptionStyle}>{detailDiagnosis.description}</div>

              {isCanceladoSefaz(detailSummary) && detailDiagnosis.id !== "cancelado_sefaz" ? (
                // PO1-3071: alerta compartilhado pela auditoria e pela conferência
                // de fatura (as duas abrem este modal). Sem data e sem protocolo.
                  // QA 13/09: quando o proprio diagnostico JA e o de cancelamento,
                  // a faixa repetiria o mesmo texto logo abaixo dele. Ela fica para os
                  // casos em que o diagnostico e outro (ex.: diferenca financeira), para
                  // o cancelamento nao passar batido.
                <div
                  className="cte-audit-detail-cancelado-sefaz"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    flexWrap: "wrap",
                    margin: "0 0 0.85rem",
                    padding: "0.6rem 0.75rem",
                    borderRadius: 10,
                    background: "var(--everlog-status-danger-bg)",
                    border: "1px solid var(--everlog-status-danger-border)",
                  }}
                >
                  <CanceladoSefazBadge />
                  <span style={{ fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>
                    {CANCELADO_SEFAZ_TITLE}
                  </span>
                </div>
              ) : null}

              <div
                className="cte-audit-detail-metrics"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                  gap: "0.75rem",
                }}
              >
                <DetailMetric
                  label="Frete a receber"
                  value={formatCurrency(detailSummary.valorFreteReceber)}
                  color={AUTOPILOT_COLORS.title}
                />
                <DetailMetric
                  label="Simulado"
                  value={formatCurrency(detailAuditoriaFinanceira?.resumo?.totalSimulado ?? detailSummary.valorFreteSimulado)}
                  color={AUTOPILOT_COLORS.accent}
                />
                <DetailMetric
                  label="Frete combinado"
                  value={formatCurrency(detailSummary.valorFreteCombinado)}
                  color={AUTOPILOT_COLORS.statusInfo}
                />
                <DetailMetric
                  label="Diferença"
                  value={formatCurrency(detailAuditoriaFinanceira?.resumo?.diferenca ?? detailSummary.valorDivergencia)}
                  color={detailFinancialDifferenceColor}
                />
              </div>
            </div>

            <div className="cte-audit-detail-tabs" style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              {detailTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setDetailTab(tab.id)}
                  className={`cte-audit-detail-tab${detailTab === tab.id ? " is-active" : ""}`}
                  style={detailTabStyle(detailTab === tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {detailTab === "visao-geral" ? (
              <>
                <SectionCard style={{ ...overviewSectionCardStyle, marginBottom: "1rem" }}>
                  <Row>
                    <Col lg={7} style={{ marginBottom: 12 }}>
                      <div style={overviewPanelStyle}>
                        <div style={overviewPanelEyebrowStyle}>Cadastro</div>
                        <div style={overviewPanelTitleStyle}>Identificação e contexto</div>
                        <div style={overviewPanelDescriptionStyle}>
                          Dados-base do documento para reconhecer rapidamente o CTe e seu contexto administrativo.
                        </div>

                        <div className="cte-audit-detail-party-summary">
                          <div className="cte-audit-detail-party-summary__field cte-audit-detail-party-summary__field--main">
                            <div style={overviewFieldLabelStyle}>Transportadora</div>
                            <div style={overviewFieldMutedValueStyle}>
                              {detailPartes?.transportadora?.fantasia || detailSummary.transportadora || "-"}
                            </div>
                          </div>
                          <div className="cte-audit-detail-party-summary__field">
                            <div style={overviewFieldLabelStyle}>CNPJ/CPF</div>
                            <div style={overviewFieldMutedValueStyle}>{detailPartes?.transportadora?.cnpjCpf || "-"}</div>
                          </div>
                          <div className="cte-audit-detail-party-summary__field">
                            <div style={overviewFieldLabelStyle}>Localidade</div>
                            <div style={overviewFieldMutedValueStyle}>
                              {buildLocationDisplay(detailPartes?.transportadora?.cidade, detailPartes?.transportadora?.uf)}
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                            gap: "0.75rem",
                            marginBottom: "1rem",
                          }}
                        >
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Emissão</div>
                            <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataEmissao)}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Modal</div>
                            <div style={overviewFieldMutedValueStyle}>{detailSummary.modal || "-"}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Tipo do documento</div>
                            <div style={overviewFieldMutedValueStyle}>
                              {ehNfse(detailSummary.modelo)
                                ? "NFse"
                                : detailSummary.tipoConhecimento || detailSummary.tipoCte || "-"}
                            </div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Fatura</div>
                            <div style={overviewFieldMutedValueStyle}><FaturaDoCteResumo fatura={detailFatura} /></div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Pago</div>
                            <div style={overviewFieldMutedValueStyle}>{formatBooleanLabel(detailSummary.pago)}</div>
                          </div>
                          {detailSummary.idAglutinacaoNotaFiscal ? (
                            <div
                              style={{
                                ...overviewMiniMetricStyle,
                                borderColor: "rgba(253, 93, 147, 0.28)",
                                background: "rgba(253, 93, 147, 0.08)",
                              }}
                            >
                              <div style={overviewFieldLabelStyle}>Aglutinação</div>
                              <div style={{ ...overviewFieldValueStyle, color: AUTOPILOT_COLORS.statusDanger }}>
                                #{detailSummary.idAglutinacaoNotaFiscal}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div style={overviewFieldGridStyle}>
                          <div style={{ ...overviewFieldStyle, gridColumn: "1 / -1" }}>
                            <div style={overviewKeyHeaderStyle}>
                              <div style={overviewFieldLabelStyle}>Chave de acesso</div>
                              <button
                                type="button"
                                id={copyButtonId}
                                onClick={handleCopyDetailAccessKey}
                                style={overviewCopyButtonStyle}
                              >
                                Copiar
                              </button>
                              <UncontrolledTooltip placement="top" target={copyButtonId} delay={{ show: 150, hide: 0 }}>
                                {detailKeyCopyState === "copied"
                                  ? "Chave copiada"
                                  : detailKeyCopyState === "error"
                                    ? "Não foi possível copiar"
                                    : "Copiar chave completa"}
                              </UncontrolledTooltip>
                            </div>
                            <div
                              style={{
                                ...overviewFieldMutedValueStyle,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {detailSummary.chaveAcesso || "-"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>

                    <Col lg={5} style={{ marginBottom: 12 }}>
                      <div style={overviewPanelStyle}>
                        <div style={overviewPanelEyebrowStyle}>Operação</div>
                        <div style={overviewPanelTitleStyle}>Panorama operacional</div>
                        <div style={overviewPanelDescriptionStyle}>
                          Origem, destino, entrega e volume associado ao documento.
                        </div>

                        <div style={overviewRouteCardStyle}>
                          <div style={overviewRouteLabelStyle}>Rota principal</div>
                          <div style={overviewRouteValueStyle}>{detailOriginLabel} → {detailDestinationLabel}</div>
                        </div>

                        <div style={overviewMiniMetricGridStyle}>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Peso CTe</div>
                            <div style={overviewFieldMutedValueStyle}>{formatWeight(detailSummary.pesoCte)}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Entrega prevista</div>
                            <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataPrevistaEntrega)}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Entrega simulada</div>
                            <div style={overviewFieldMutedValueStyle}>{formatDateLabel(detailSummary.dataPrometidaEntrega)}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Peso NFes</div>
                            <div style={overviewFieldMutedValueStyle}>{formatWeight(detailSummary.pesoTotal)}</div>
                          </div>
                          <div style={overviewMiniMetricStyle}>
                            <div style={overviewFieldLabelStyle}>Mercadoria</div>
                            <div style={overviewFieldMutedValueStyle}>{formatCurrency(detailSummary.valorTotalMercadoria)}</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </SectionCard>
              </>
            ) : null}

            {detailTab === "auditoria" ? (
              <>
                <SectionCard
                  title="Consistência da base do cálculo"
                  description="Comece pelos indicadores principais para entender se o caso é divergência real, ausência de dado ou apenas diferença pontual."
                  style={{ marginBottom: "1rem" }}
                >
                  <Row>
                    <Col md={4} style={{ marginBottom: 12 }}>
                      <DetailMetric label="Checks OK" value={detailAuditoria?.resumo?.ok || 0} color="#00bf9a" />
                    </Col>
                    <Col md={4} style={{ marginBottom: 12 }}>
                      <DetailMetric label="Divergências" value={detailAuditoria?.resumo?.divergente || 0} color="#fd5d93" />
                    </Col>
                    <Col md={4} style={{ marginBottom: 12 }}>
                      <DetailMetric label="Sem dado" value={detailAuditoria?.resumo?.semDado || 0} color="#ffb86b" />
                    </Col>
                  </Row>
                </SectionCard>

                <SectionCard
                  title="Checks da composição"
                  description="Valide peso, valor de mercadoria, quantidade de NFes e origem/destino antes de aprofundar na comparação financeira."
                >
                  <Table size="sm" responsive style={{ marginBottom: 0 }}>
                    <colgroup>
                      <col style={{ width: "23%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "12%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={tableHeadCellStyle}>Regra</th>
                        <th style={tableHeadCellStyle}>Documento</th>
                        <th style={tableHeadCellStyle}>NFes vinculadas</th>
                        <th style={tableHeadCellStyle}>Diferença</th>
                        <th style={tableHeadCellStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailAuditoria?.checks?.length ? (
                        detailAuditoria.checks.map((check) => (
                          <tr key={check.id}>
                            <td
                              style={{
                                ...detailTableCellStyle,
                                fontWeight: 700,
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              {check.label || "-"}
                            </td>
                            <td style={{ ...detailTableCellStyle, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                              {formatAuditValue(
                                check.cteValor,
                                check.id === "peso"
                                  ? "peso"
                                  : check.id === "valor_mercadoria"
                                    ? "currency"
                                    : null
                              )}
                            </td>
                            <td style={{ ...detailTableCellStyle, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                              {check.id === "peso" ? (
                                <AuditWeightCell check={check} />
                              ) : (
                                formatAuditValue(
                                  check.nfesValor,
                                  check.id === "valor_mercadoria" ? "currency" : null
                                )
                              )}
                            </td>
                            <td style={{ ...detailTableCellStyle, whiteSpace: "nowrap" }}>
                              {formatAuditValue(
                                check.diferenca,
                                check.id === "peso"
                                  ? "peso"
                                  : check.id === "valor_mercadoria"
                                    ? "currency"
                                    : null
                              )}
                            </td>
                            <td style={detailTableCellStyle}>
                              <AuditStatusBadge status={check.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                            Nenhum check de auditoria disponível para este CTe.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </SectionCard>

                <SectionCard
                  title="Composição financeira"
                  description="Compare o total a receber com o total simulado e valide os grupos que participam da composição antes de aprofundar nos itens."
                  style={{ marginTop: "1rem" }}
                >
                  <Row>
                    <Col md={8}>
                      <DetailField
                        label="Tabela de preço"
                        value={
                          <SimulatedPriceTablesValue
                            summary={detailAuditoriaFinanceira?.tabelaPrecoSimulado}
                            onOpenTabela={podeAbrirTabelaFrete(user) ? setTabelaFreteAbertaId : null}
                          />
                        }
                        valueStyle={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                      />
                    </Col>
                    <Col md={4}>
                      <DetailField label="Limite de divergência" value={<LimiteDivergenciaValue summary={detailSummary} />} />
                    </Col>
                  </Row>

                  <Table size="sm" responsive style={{ marginBottom: 0 }}>
                    <colgroup>
                      <col style={{ width: "26%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "12%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th style={tableHeadCellStyle}>Grupo</th>
                        <th style={tableHeadCellStyle}>Documento</th>
                        <th style={tableHeadCellStyle}>Simulado</th>
                        <th style={tableHeadCellStyle}>Diferença</th>
                        <th style={tableHeadCellStyle}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailFinancialGroups.length ? (
                        detailFinancialGroups.map((check) => {
                          // total_simulado é uma comparação própria, não a soma das linhas
                          // acima: separa com borda/espaço para não parecer somatório. [PO1-2491]
                          const isTotal = check.id === "total_simulado";
                          const cellStyle = isTotal
                            ? {
                                ...detailTableCellStyle,
                                borderTop: `2px solid ${AUTOPILOT_COLORS.dividerStrong}`,
                                paddingTop: "0.65rem",
                              }
                            : detailTableCellStyle;
                          return (
                            <tr key={check.id}>
                              <td style={{ ...cellStyle, fontWeight: 700 }}>
                                {check.label || "-"}
                                {check.id === "tributos" ? (
                                  <TributoIncideBadge incide={check.tributoIncideNoTotal} tipo={check.tributoTipo} />
                                ) : null}
                              </td>
                              <td style={cellStyle}>
                                {formatAuditValue(check.cteValor, "currency")}
                                {check.id === "tributos" && check.tributoOrigemCte === "componente" ? (
                                  <div
                                    title="O CTe destaca o imposto como componente do frete; esse valor entra aqui e sai de Taxas."
                                    style={{ fontSize: "0.72rem", color: "var(--everlog-card-muted)", marginTop: 2 }}
                                  >
                                    componente: {(check.tributoComponentesCte || []).join(" / ")}
                                  </div>
                                ) : null}
                              </td>
                              <td style={cellStyle}>{formatAuditValue(check.nfesValor, "currency")}</td>
                              <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                                {formatAuditValue(check.diferenca, "currency")}
                              </td>
                              <td style={cellStyle}>
                                <AuditStatusBadge status={check.status} />
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                            Nenhum comparativo financeiro disponível para este CTe.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </SectionCard>

                <SectionCard
                  title="Componentes do CT-e x simulado"
                  description="Compara os componentes recebidos no documento com a regra e a memória de cálculo do simulador. Itens sem de/para permanecem visíveis."
                  style={{ marginTop: "1rem" }}
                >
                  <FinancialComponentComparisonTable
                    rows={detailAuditoriaFinanceira?.componentesComparativo || detailAuditoriaFinanceira?.taxas?.itens}
                    formatValue={formatAuditValue}
                    headCellStyle={tableHeadCellStyle}
                    cellStyle={detailTableCellStyle}
                    onLinkComponent={onLinkComponent}
                  />
                </SectionCard>
              </>
            ) : null}

            {detailTab === "notas" ? (
              <SectionCard
                title="Notas fiscais vinculadas"
                description="Use esta aba para entender a composição do CTe e localizar a origem de uma divergência sem sair do mesmo contexto."
              >
                <div style={{ marginBottom: "0.65rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <Badge color="info">{detailNfs.length} NFe vinculada(s)</Badge>
                </div>
                <div style={{ maxHeight: "46vh", overflowY: "auto" }}>
                  <Table size="sm" responsive style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={tableHeadCellStyle}>NFe</th>
                        <th style={tableHeadCellStyle}>Série</th>
                        <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Peso</th>
                        <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor total</th>
                        <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete simulado</th>
                        <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Frete combinado</th>
                        <th style={tableHeadCellStyle}>Entrega</th>
                        <th style={tableHeadCellStyle}>Entrega simulada</th>
                        <th style={tableHeadCellStyle}>Pedido</th>
                        <th style={tableHeadCellStyle}>CNPJ remetente</th>
                        <th style={tableHeadCellStyle}>Negócio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailNfs.length === 0 ? (
                        <tr>
                          <td colSpan="11" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                            Nenhuma NFe vinculada encontrada para este CTe.
                          </td>
                        </tr>
                      ) : (
                        detailNfs.map((nota) => (
                          <tr key={nota.id}>
                            <td style={detailTableCellStyle}>
                              {nota.id && nota.numero ? (
                                <button type="button" style={nfLinkStyle} title="Ver detalhe da NFe" onClick={() => setNfAbertaId(nota.id)}>
                                  {nota.numero}
                                </button>
                              ) : (
                                nota.numero || "-"
                              )}
                            </td>
                            <td style={detailTableCellStyle}>{nota.serie || "-"}</td>
                            <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatWeight(nota.peso)}</td>
                            <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorTotal)}</td>
                            <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorFreteSimulado)}</td>
                            <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>{formatCurrency(nota.valorFreteCombinado)}</td>
                            <td style={detailTableCellStyle}>{formatDateLabel(nota.dataEntrega)}</td>
                            <td style={detailTableCellStyle}>{formatDateLabel(nota.dataEntregaSimulada)}</td>
                            <td style={detailTableCellStyle}>{nota.pedido || "-"}</td>
                            <td style={detailTableCellStyle}>{nota.cnpjRemetente || "-"}</td>
                            <td style={detailTableCellStyle}>{nota.negocio || "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </SectionCard>
            ) : null}

            {detailTab === "fatura" ? (
              <div style={{ marginTop: "0.5rem" }}>
                <FaturaDoCteAba fatura={detailFatura} />
              </div>
            ) : null}

            {detailTab === "visao-geral" ? (
              <>
                <Row>
                  <Col lg={12} style={{ marginBottom: 12 }}>
                    <SectionCard title="Remetente" description="Dados cadastrais do remetente vinculados ao CTe.">
                      <Row>
                        <Col md={4}><DetailField label="CNPJ/CPF" value={detailPartes?.remetente?.cnpjCpf} /></Col>
                        <Col md={8}><DetailField label="Nome fantasia" value={detailPartes?.remetente?.fantasia} /></Col>
                      </Row>
                      <Row>
                        <Col md={9}><DetailField label="Cidade" value={detailPartes?.remetente?.cidade} /></Col>
                        <Col md={3}><DetailField label="UF" value={detailPartes?.remetente?.uf} /></Col>
                      </Row>
                    </SectionCard>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12} style={{ marginBottom: 12 }}>
                    <SectionCard title="Componentes" description="Componentes de prestação de serviço usados na composição do CTe.">
                      <Table size="sm" responsive style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th style={tableHeadCellStyle}>Nome</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailComponentes.length === 0 ? (
                            <tr>
                              <td colSpan="2" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                Nenhum componente encontrado para este CTe.
                              </td>
                            </tr>
                          ) : (
                            detailComponentes.map((item) => (
                              <tr key={item.id}>
                                <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {formatCurrency(item.valor)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </SectionCard>
                  </Col>

                  <Col lg={12} style={{ marginBottom: 12 }}>
                    <SectionCard title="Impostos" description="Leitura tributária do CTe para apoiar a conferência e a análise de cálculo.">
                      <Table size="sm" responsive style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th style={tableHeadCellStyle}>Nome</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Base cálculo ICMS</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Valor ICMS</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Alíquota</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailImpostos.length === 0 ? (
                            <tr>
                              <td colSpan="4" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                Nenhum imposto encontrado para este CTe.
                              </td>
                            </tr>
                          ) : (
                            detailImpostos.map((item) => (
                              <tr key={item.id}>
                                <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {formatCurrency(item.valorBaseCalculoIcms)}
                                </td>
                                <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {formatCurrency(item.valorIcms)}
                                </td>
                                <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {formatPercent(item.aliquotaIcms)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </SectionCard>
                  </Col>
                </Row>

                <Row>
                  <Col lg={12} style={{ marginBottom: 12 }}>
                    <SectionCard title="Carga" description="Quantidade de carga, unidade e medida usadas no documento.">
                      <Table size="sm" responsive style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th style={tableHeadCellStyle}>Nome</th>
                            <th style={tableHeadCellStyle}>Medida</th>
                            <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>Quantidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailCarga.length === 0 ? (
                            <tr>
                              <td colSpan="3" style={{ ...detailTableCellStyle, textAlign: "center", padding: "1rem" }}>
                                Nenhuma quantidade de carga encontrada para este CTe.
                              </td>
                            </tr>
                          ) : (
                            detailCarga.map((item) => (
                              <tr key={item.id}>
                                <td style={detailTableCellStyle}>{item.nome || "-"}</td>
                                <td style={detailTableCellStyle}>{item.medida || "-"}</td>
                                <td style={{ ...detailTableCellStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                                  {item.quantidade != null ? Number(item.quantidade).toLocaleString("pt-BR") : "-"}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </SectionCard>
                  </Col>

                  <Col lg={12} style={{ marginBottom: 12 }}>
                    <SectionCard title="Informações complementares" description="Campos auxiliares úteis na conferência e no tratamento de exceções.">
                      <Row>
                        <Col md={12}><DetailField label="CFOP" value={detailAdicionais?.cfop} /></Col>
                      </Row>
                      <Row>
                        <Col md={12}><DetailField label="Produto predominante" value={detailAdicionais?.produtoPredominante} /></Col>
                      </Row>
                      <Row>
                        <Col md={12}><DetailField label="Outras características" value={detailAdicionais?.outrasCaracteristicas} /></Col>
                      </Row>
                    </SectionCard>
                  </Col>
                </Row>
              </>
            ) : null}
          </>
        ) : (
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem", padding: "0.25rem 0 0.75rem" }}>
            Nenhum detalhe disponível para este CTe.
          </div>
        )}
      </ModalBody>
      {tabelaFreteAbertaId && (
        <TabelaFreteModal id={tabelaFreteAbertaId} onClose={() => setTabelaFreteAbertaId(null)} />
      )}
      {/* PO1-3478: detalhe da NFe sem sair do CT-e. */}
      <NotaFiscalDetailModal
        isOpen={Boolean(nfAbertaId)}
        notaFiscalId={nfAbertaId}
        onClose={() => setNfAbertaId(null)}
      />
    </Modal>
  );
}
